import React, { useCallback } from 'react';
import partial from 'lodash-es/partial';
import { Box, Button, HStack, useDisclosure } from '@televet/kibble-ui';
import { RadioButtonGroup } from '@televet/kibble-ui/build/components/RadioButtonGroup';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { useAutomationEditorView } from 'pages/Automations/hooks/useAutomationEditorView';
import { AutomationEditorView } from 'pages/Automations/types/AutomationEditorView';
import { useSaveAutomationDraft } from 'pages/Automations/hooks';
import { ActivateAutomationModal } from 'pages/Automations/components';
import { useValidateAutomationDraft } from 'pages/Automations/hooks/useValidateAutomationDraft';
import { ValidationActionWord } from 'pages/Automations/types/AutomationValidation';
import { updateErrorAlertIsVisible, updateValidationActionWord } from 'pages/Automations/state/automationsSlice';

export const AutomationsHeader = (): JSX.Element => {
  const { saveAutomationDraft, hasUnsavedChanges } = useSaveAutomationDraft();
  const { currentEditorView, setCurrentEditorView } = useAutomationEditorView();
  const isPublished = useAppSelector((state) => state.automations.automationDraft.isPublished);
  const activateAutomationModalState = useDisclosure();
  const { validateAutomationDraft } = useValidateAutomationDraft();
  const dispatch = useAppDispatch();

  const handleActivateButtonClick = useCallback((): void => {
    dispatch(updateValidationActionWord(ValidationActionWord.Saving));
    const automationHasErrors = validateAutomationDraft();

    if (automationHasErrors) {
      dispatch(updateErrorAlertIsVisible(true));
      return;
    }

    if (hasUnsavedChanges) {
      activateAutomationModalState.onOpen();
      return;
    }

    saveAutomationDraft({ isPublished: true });
  }, [saveAutomationDraft, dispatch, validateAutomationDraft, hasUnsavedChanges, activateAutomationModalState]);

  return (
    <>
      <HStack
        background="background.default"
        boxShadow="md"
        className="Automations__Header"
        justifyContent="space-between"
        py={3}
        px={5}
        position="absolute"
        zIndex="2"
        width="inherit"
      >
        <Box>
          {currentEditorView === AutomationEditorView.Compose && (
            <Button
              iconName="chevronLeft"
              variant="secondarySubtle"
              onClick={partial(setCurrentEditorView, AutomationEditorView.Settings)}
            >
              Previous
            </Button>
          )}
        </Box>
        <Box
          className="Automations__HeaderCenter"
          position="absolute"
          left="50%"
          top="50%"
          transform="translate(-60%, -60%)"
        >
          {/* TODO: CU-86dvx98k1 Let's make sure to update the size of these back to sm */}
          <RadioButtonGroup
            label={''}
            onChange={(value): void => setCurrentEditorView(value)}
            options={[
              {
                label: 'Settings',
                value: AutomationEditorView.Settings,
              },
              {
                label: 'Compose',
                value: AutomationEditorView.Compose,
              },
            ]}
            selected={currentEditorView || AutomationEditorView.Settings}
          />
        </Box>
        <Box className="Automations__HeaderRight">
          <HStack className="Automations__HeaderRight-Buttons" spacing={3}>
            {currentEditorView === AutomationEditorView.Compose &&
              (isPublished ? (
                <Button
                  variant="tertiary"
                  onClick={partial(saveAutomationDraft, { isPublished: false })}
                  className="Automations__HeaderRight-Buttons--Deactivate"
                >
                  Deactivate
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleActivateButtonClick}
                  className="Automations__HeaderRight-Buttons--Activate"
                >
                  Activate
                </Button>
              ))}
            {currentEditorView === AutomationEditorView.Settings && (
              <>
                {isPublished ? (
                  <Button
                    variant="tertiary"
                    onClick={partial(saveAutomationDraft, { isPublished: false })}
                    className="Automations__HeaderRight-Buttons--Deactivate"
                  >
                    Deactivate
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={handleActivateButtonClick}
                    className="Automations__HeaderRight-Buttons--Activate"
                  >
                    Activate
                  </Button>
                )}
                <Button
                  iconName="chevronRight"
                  iconPosition="right"
                  onClick={partial(setCurrentEditorView, AutomationEditorView.Compose)}
                >
                  Next
                </Button>
              </>
            )}
          </HStack>
        </Box>
      </HStack>
      <ActivateAutomationModal
        isOpen={activateAutomationModalState.isOpen}
        onClose={activateAutomationModalState.onClose}
      />
    </>
  );
};
